import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  AccountCircle as AccountCircleIcon,
  ArrowBack as ArrowBackIcon,
  Handshake as HandshakeIcon,
  HelpOutline as HelpOutlineIcon,
  LightbulbOutlined as LightbulbIcon,
  People as PeopleIcon,
  PlayCircleOutline as PlayCircleIcon,
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  WarningAmberOutlined as WarningIcon,
  WorkOutline as WorkOutlineIcon
} from '@mui/icons-material';

import CustomerLayout from '../../../components/customer/CustomerLayout';
import { useCustomerPortal } from '../../../contexts/CustomerPortalContext';
import {
  Audience,
  Block,
  HELP_SECTIONS,
  Item,
  Section,
  SectionIcon,
  SectionId,
  audienceMatches,
  resolveLocale
} from './helpContent';

/**
 * GUÍA GLOBAL del portal del cliente (/customer/help).
 *
 * Una sola página con índice lateral pegajoso y secciones ancladas: se lee de
 * arriba abajo la primera vez y después se entra por el índice o por un enlace
 * directo (#creditos, #almacenamiento…) — que es justo lo que hace la ayuda
 * contextual de «Mi cuenta» (AccountHelpPanel) al decir «saber más».
 *
 * El contenido editorial NO vive aquí sino en helpContent.ts (bilingüe por
 * bloque), como el manual del employee-portal: esta página solo sabe MAQUETAR
 * bloques. Añadir una sección es tocar el contenido, nunca este fichero.
 */

/** Alto de la barra pegajosa del portal: los anclajes tienen que descontarlo o
 *  el título de la sección queda oculto bajo el navbar. */
const NAV_OFFSET = 88;

const SECTION_ICONS: Record<SectionIcon, React.ReactElement> = {
  start: <PlayCircleIcon fontSize="small" />,
  employee: <SmartToyIcon fontSize="small" />,
  account: <AccountCircleIcon fontSize="small" />,
  users: <PeopleIcon fontSize="small" />,
  hire: <WorkOutlineIcon fontSize="small" />,
  credits: <WalletIcon fontSize="small" />,
  storage: <StorageIcon fontSize="small" />,
  partner: <HandshakeIcon fontSize="small" />,
  faq: <HelpOutlineIcon fontSize="small" />
};

/** Lista «rótulo → explicación». Se usa tal cual para `list` y numerada para
 *  `steps`: la única diferencia es la viñeta, no la estructura. */
const ItemList: React.FC<{ items: Item[]; numbered?: boolean }> = ({ items, numbered }) => {
  const theme = useTheme();
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, mb: 2.5, p: 0 }}>
      {items.map((item, idx) => (
        <Box
          component="li"
          key={item.k}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.9 }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              mt: 0.35,
              width: 22,
              height: 22,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              bgcolor: numbered ? 'primary.main' : alpha(theme.palette.primary.main, 0.12),
              color: numbered ? 'primary.contrastText' : 'primary.main'
            }}
          >
            {numbered ? idx + 1 : '•'}
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            <Typography component="span" variant="body1" fontWeight={700} color="text.primary">
              {item.k}
            </Typography>
            {' — '}
            {item.v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const BlockView: React.FC<{ block: Block; loc: 'es' | 'en' }> = ({ block, loc }) => {
  switch (block.kind) {
    case 'h':
      return (
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1.25 }}>
          {block[loc]}
        </Typography>
      );
    case 'p':
      return (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.75 }}>
          {block[loc]}
        </Typography>
      );
    case 'tip':
      return (
        <Alert severity="info" icon={<LightbulbIcon fontSize="inherit" />} sx={{ mb: 2.5 }}>
          {block[loc]}
        </Alert>
      );
    case 'warn':
      return (
        <Alert severity="warning" icon={<WarningIcon fontSize="inherit" />} sx={{ mb: 2.5 }}>
          {block[loc]}
        </Alert>
      );
    case 'list':
      return <ItemList items={block[loc]} />;
    case 'steps':
      return <ItemList items={block[loc]} numbered />;
    default:
      return null;
  }
};

const SectionView: React.FC<{
  section: Section;
  loc: 'es' | 'en';
}> = ({ section, loc }) => {
  const theme = useTheme();
  const copy = section[loc];
  return (
    <Box
      component="section"
      id={section.id}
      sx={{ scrollMarginTop: `${NAV_OFFSET}px`, mb: 6 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main'
          }}
        >
          {SECTION_ICONS[section.icon]}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          {copy.title}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ mb: 2.5, lineHeight: 1.7, color: 'text.primary', fontWeight: 500 }}
      >
        {copy.lead}
      </Typography>
      {section.blocks.map((block, idx) => (
        <BlockView key={`${section.id}-${idx}`} block={block} loc={loc} />
      ))}
      <Divider sx={{ mt: 4 }} />
    </Box>
  );
};

const CustomerHelpPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const loc = resolveLocale(i18n.language);
  const navigate = useNavigate();
  const location = useLocation();
  const { managedByReseller, loading } = useCustomerPortal();
  const managed = !!managedByReseller;
  const [active, setActive] = useState<SectionId>(HELP_SECTIONS[0].id);

  // Filtra la guía por el tipo de cliente ANTES de pintar: un cliente directo
  // no ve la sección del partner ni las frases «para más créditos, pídeselo al
  // partner»; uno gestionado no ve «Planes / pasarela / facturación». Mientras
  // no sabemos aún (loading) mostramos SOLO lo común ('all'): nunca contenido
  // contradictorio ni un salto de una variante a la otra al resolverse.
  const visible = useCallback(
    (audience?: Audience) => (loading ? !audience || audience === 'all' : audienceMatches(audience, managed)),
    [loading, managed]
  );

  const sections: Section[] = useMemo(
    () =>
      HELP_SECTIONS.filter((s) => visible(s.audience)).map((s) => ({
        ...s,
        blocks: s.blocks.filter((b) => visible(b.audience))
      })),
    [visible]
  );

  // Clave estable de las secciones visibles: reengancha el observador solo
  // cuando cambia el conjunto (loading→resuelto, o directo↔gestionado).
  const sectionKey = sections.map((s) => s.id).join('|');

  const scrollTo = useCallback((id: SectionId) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  // Entrada por enlace directo (#creditos, #almacenamiento…): la ayuda
  // contextual de «Mi cuenta» enlaza así, y el navegador no sabe descontar el
  // navbar pegajoso, de ahí el scroll a mano. El timeout deja pintar antes.
  useEffect(() => {
    const hash = location.hash.replace('#', '') as SectionId;
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    setActive(hash);
    const timer = window.setTimeout(() => scrollTo(hash), 60);
    return () => window.clearTimeout(timer);
  }, [location.hash, scrollTo]);

  // Sección activa del índice: gana la primera que cruza la franja alta del
  // viewport, que es la que el lector tiene delante de los ojos.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id as SectionId);
      },
      { rootMargin: `-${NAV_OFFSET}px 0px -55% 0px`, threshold: 0 }
    );
    sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // sectionKey cambia con el conjunto visible; reengancha el observador a las
    // secciones que de verdad están en el DOM.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  return (
    <CustomerLayout>
      {/* Héroe: la tesis de la guía en una frase, sin ruido */}
      <Box sx={{ background: 'linear-gradient(180deg, #f4f9ff 0%, #ffffff 100%)', pt: 6, pb: 4 }}>
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            color="primary"
            sx={{ fontWeight: 800, letterSpacing: '0.14em' }}
          >
            {t('customer.help.eyebrow', 'Guía del portal')}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.12,
              mt: 1,
              mb: 1.5,
              fontSize: { xs: '1.9rem', sm: '2.3rem', md: '2.6rem' }
            }}
          >
            {t('customer.help.heroTitle', 'Tu nuevo empleado no es una app.')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 720, fontSize: '1.05rem', lineHeight: 1.7 }}
          >
            {t(
              'customer.help.heroLead',
              'Es un compañero digital que trabaja para tu negocio. Esta guía te explica, sin tecnicismos, qué contratas, quién lo usa, qué consume y quién paga qué — para que entiendas el producto sin que nadie te lo cuente.'
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/customer/account')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('customer.help.backToAccount', 'Volver a Mi cuenta')}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {t('customer.help.readingTime', 'Lectura completa ≈ 10 min')}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Grid container spacing={4}>
          {/* Índice lateral pegajoso (en móvil va arriba, sin pegar) */}
          <Grid item xs={12} md={3.5}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 1,
                position: { md: 'sticky' },
                top: { md: `${NAV_OFFSET}px` }
              }}
            >
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, letterSpacing: 1.1, px: 1.5, color: 'text.secondary' }}
              >
                {t('customer.help.contents', 'En esta guía')}
              </Typography>
              <List dense disablePadding>
                {sections.map((section) => (
                  <ListItemButton
                    key={section.id}
                    selected={active === section.id}
                    onClick={() => navigate(`/customer/help#${section.id}`)}
                    sx={{
                      borderRadius: 2,
                      '&.Mui-selected': { bgcolor: 'action.selected' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: active === section.id ? 'primary.main' : 'text.secondary' }}>
                      {SECTION_ICONS[section.icon]}
                    </ListItemIcon>
                    <ListItemText
                      primary={section[loc].title}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: active === section.id ? 700 : 500
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Cuerpo de la guía */}
          <Grid item xs={12} md={8.5}>
            <Box sx={{ pt: 1 }}>
              {sections.map((section) => (
                <SectionView key={section.id} section={section} loc={loc} />
              ))}
            </Box>

            {/* Cierre: a dónde ir si la guía no resolvió la duda */}
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <HelpOutlineIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('customer.help.stillStuckTitle', '¿Te queda alguna duda?')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                    {managed
                      ? t(
                          'customer.help.stillStuckTextManaged',
                          'Tu cuenta la gestiona un partner: escríbele a él para cualquier duda, recarga de créditos o ampliación de cupo. Lleva tus créditos, tu cupo, tu espacio y tu facturación. Tienes su nombre y su correo en el aviso del portal.'
                        )
                      : t(
                          'customer.help.stillStuckTextDirect',
                          'Si no has encontrado la respuesta aquí, escríbenos desde Soporte y te ayudamos con lo que necesites.'
                        )}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/customer/support')}
                    sx={{ textTransform: 'none', boxShadow: 'none', fontWeight: 600 }}
                  >
                    {t('customer.help.goToSupport', 'Ir a Soporte')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </CustomerLayout>
  );
};

export default CustomerHelpPage;
